import React, { useContext } from 'react';
import { useTranslations } from '@vp/om-translations';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import { TaxAwarePriceRow } from '@vp/component-library';
import useSiteData from '../../../hooks/useSiteData';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { currencyLocaleFormatter } from '../../../util/currencyFormatter';
import {
  DETAILED_TAX_DISPLAY,
  ITEM_LEVEL,
} from '../../../constants/taxDisplayOptions';

/**
 * Responsible for rendering the total for a lineItem
 * 1. Gets the product pricing data from context
 * 2. Generates the tax aware product price for a given lineItemId
 * 3. Displays the itemTotal
 *
 * Notes:
 * - Expected that this is rendered within a LineItemDataProvider to access item properties
 * - uses om-tax-display library to apply tax display rules (tax-inclusivity and display formatting).
 */
export const ItemTotalSummary = () => {
  const {
    itemIdToTaxInclusiveAndExclusivePricesMap,
    currency,
    fractionDigits,
  } = useContext(OrderDetailsContext);
  const { locale } = useSiteData();
  const [lineItemMessages] = useTranslations(['lineItemMessages']);
  const localeForCurrency = currencyLocaleFormatter(locale);
  const { id } = useLineItemData();

  const taxAwareItemPrice = aggregateTaxAwarePrices(
    itemIdToTaxInclusiveAndExclusivePricesMap[id],
    ITEM_LEVEL,
    DETAILED_TAX_DISPLAY,
    [],
    {} // OrderDetails doesn't respect the VAT display toggle, so we pass an empty object for taxDisplayContext
  );
  return (
    <TaxAwarePriceRow
      rowLabel={lineItemMessages.itemTotalField}
      labelFormat={{ fontSize: 2 }}
      priceFormat={{
        fontSize: 2,
        fontWeight: 'bold',
      }}
      fractionDigits={fractionDigits}
      listCents={taxAwareItemPrice.taxAwareListCents}
      discountedCents={taxAwareItemPrice.taxAwareDiscountedCents}
      currencyCode={currency}
      locale={localeForCurrency}
      taxes={[
        ...taxAwareItemPrice.includedTaxes,
        ...taxAwareItemPrice.excludedTaxes,
      ]}
    />
  );
};
